/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Breadcrumbs as BreadcrumbsType } from "@/types/data";
import { StrapiTerminusArticle } from "@/types/strapi";
import { ColumnStandardCount, columnCounts } from "@/types/tokens/grid";

import { BreakpointBases } from "@/tokens/breakpoints";
import { Spacing } from "@/tokens/spacing";

import { Divider } from "@/ui/atoms/divider";
import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { SubgridContainer } from "@/ui/atoms/subgrid_container";
import { Text } from "@/ui/atoms/text";
import { Breadcrumbs } from "@/ui/molecules/breadcrumbs";
import { ArticleListItem } from "@/ui/molecules/terminus_article_list_item";
import {
    TerminusSidebar,
    TerminusSidebarProps,
} from "@/ui/organisms/terminus_sidebar";

import { buildStylesByBreakpoint } from "@/util/style_util";
import { getTemplateColumns } from "@/util/tokens/layout_util";

interface TerminusLayoutProps {
    breadcrumbs?: BreadcrumbsType;
    children?: React.ReactNode;
    leftColumn?: TerminusSidebarProps;
    rightColumn?: TerminusSidebarProps;
    terminusSlugArticleList?: {
        articles: StrapiTerminusArticle[];
    };
}

export const TerminusLayout = (props: TerminusLayoutProps) => {
    const {
        breadcrumbs,
        children,
        leftColumn,
        rightColumn,
        terminusSlugArticleList,
    } = props;

    /**
     * Elements
     */
    const _breadcrumbs: BreadcrumbsType = {
        ...{
            level1: {
                children: "Terminus",
                href: "/terminus",
            },
        },
        ...breadcrumbs,
    };

    /**
     * Styles
     */
    const terminusGridStyles = css(
        {
            maxWidth: `${BreakpointBases.EXTRA_LARGE}rem`,
            width: "100%",
        },
        buildStylesByBreakpoint("gridTemplateColumns", {
            extraSmall: getTemplateColumns(columnCounts.small),
            medium: `${leftColumn ? "12rem " : ""}
              repeat(${columnCounts.small}, 1fr)
              ${rightColumn ? " 12rem" : ""}
            `,
            large: `${leftColumn && "13rem "}
              repeat(${columnCounts.small}, 1fr)
              ${rightColumn && " 13rem"}
            `,
        }),
        buildStylesByBreakpoint("columnGap", {
            medium: Spacing["spacing-8"],
        }),
    );

    const terminusSlugArticleListStyles = css(
        buildStylesByBreakpoint("gridRow", {
            extraSmall: 2,
            medium: 3,
        }),
    );

    return (
        <>
            {_breadcrumbs && (
                <div>
                    <Breadcrumbs breadcrumbs={_breadcrumbs} isLegacy={false} />
                </div>
            )}

            <GridContainer className={terminusGridStyles}>
                {leftColumn && (
                    <GridColumn
                        className={css(
                            buildStylesByBreakpoint("gridRow", {
                                extraSmall: 4,
                                medium: "1 / span 3", // ensures the left sidebar sticks all the way down on [slug]
                            }),
                        )}
                        columnSpan={{
                            extraSmall: "full",
                            medium: 1,
                        }}
                        tag="nav"
                    >
                        <TerminusSidebar {...leftColumn} />
                    </GridColumn>
                )}

                <SubgridContainer
                    columnSpan={{
                        extraSmall: "full",
                        medium: 6,
                    }}
                    columnStart={{
                        extraSmall: 1,
                        medium: 2,
                    }}
                >
                    {children}
                </SubgridContainer>

                {terminusSlugArticleList?.articles?.length && (
                    <GridColumn
                        className={terminusSlugArticleListStyles}
                        columnSpan={{
                            extraSmall: "full",
                            medium: 7,
                        }}
                        columnStart={{
                            extraSmall: 1,
                            medium: 2,
                        }}
                    >
                        <Text
                            fontSize="TerminusArticleListHeadline"
                            marginBottom="TerminusArchiveTitleMarginBottom"
                            marginTop="TerminusArchiveTitleMarginTop"
                            tag="h2"
                        >
                            Related Articles
                        </Text>

                        <Divider />

                        {terminusSlugArticleList.articles.map(
                            (article, index) => (
                                <ArticleListItem
                                    article={article}
                                    index={index}
                                    key={article.Slug}
                                />
                            ),
                        )}
                    </GridColumn>
                )}

                {rightColumn && (
                    <GridColumn
                        className={css(
                            buildStylesByBreakpoint("gridRow", {
                                extraSmall: 3,
                                medium: 1,
                            }),
                        )}
                        columnSpan={{
                            extraSmall: "full",
                            medium: 1,
                        }}
                        columnStart={{
                            extraSmall: 1,
                            medium: (columnCounts.small +
                                2) as ColumnStandardCount,
                        }}
                        tag="nav"
                    >
                        <TerminusSidebar {...rightColumn} />
                    </GridColumn>
                )}
            </GridContainer>
        </>
    );
};
